import React, { ReactElement, useState } from 'react';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import {
  boldMd,
  boldSm,
  regularMd,
  regularSm,
  regularXl,
  regularLg,
} from '../../../constants/location';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import { RuleSuggestion } from '../../../api/firebase/function/handleSuggestPatternRule';

export type ApprovedSuggestion = {
  field_name: string;
  suggested_rule: string;
};

type Props = {
  open: boolean;
  suggestions: RuleSuggestion[];
  loading: boolean;
  onClose: () => void;
  onApply: (approved: ApprovedSuggestion[]) => Promise<void>;
  /**
   * 子パターンとして保存する（#2967）。
   * 未指定ならボタンを出さない（機能が無効な企業・権限のないユーザー）
   */
  onSaveAsSubPattern?: (approved: ApprovedSuggestion[]) => void;
};

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      maxWidth: '720px',
      width: '100%',
      '@media screen and (max-width: 960px)': {
        padding: '24px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  subtitle: {
    ...regularMd,
    color: gray80,
    marginBottom: '24px',
  },
  suggestionCard: {
    border: `1px solid ${gray40}`,
    borderRadius: '8px',
    padding: '16px',
  },
  fieldName: {
    ...boldMd,
    marginBottom: '12px',
  },
  ruleLabel: {
    ...boldSm,
    color: gray80,
    marginBottom: '4px',
  },
  ruleText: {
    ...regularMd,
    whiteSpace: 'pre-wrap' as const,
  },
  emptyRule: {
    ...regularMd,
    color: gray80,
    fontStyle: 'italic',
  },
  buttonWrapper: {
    display: 'flex',
    gap: '8px',
    marginTop: '24px',
    justifyContent: 'flex-end',
    '@media screen and (max-width:960px)': {
      flexDirection: 'column-reverse',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    minWidth: '120px',
  },
  applyButton: {
    ...primaryButtonBackgroundColor,
    height: '44px',
    minWidth: '120px',
  },
  selectAllButton: {
    ...tertiaryButtonBackgroundColor,
    height: '36px',
  },
  noSuggestionsBox: {
    textAlign: 'center' as const,
    padding: '32px 0',
  },
};

const PatternRuleImprovementModal = ({
  open,
  suggestions,
  loading,
  onClose,
  onApply,
  onSaveAsSubPattern,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();
  const [approvedSet, setApprovedSet] = useState<Set<string>>(
    () => new Set(suggestions.map(s => s.field_name)),
  );
  const [editedRules, setEditedRules] = useState<Record<string, string>>(() =>
    Object.fromEntries(suggestions.map(s => [s.field_name, s.suggested_rule])),
  );
  const [applying, setApplying] = useState(false);

  const toggleApproval = (fieldName: string): void => {
    setApprovedSet(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return next;
    });
  };

  const handleSelectAll = (): void => {
    setApprovedSet(new Set(suggestions.map(s => s.field_name)));
  };

  const handleDeselectAll = (): void => {
    setApprovedSet(new Set());
  };

  const handleRuleChange = (fieldName: string, value: string): void => {
    setEditedRules(prev => ({ ...prev, [fieldName]: value }));
  };

  // 承認された提案（編集後の内容）を組み立てる。親への適用と子パターンの保存で共用する
  const buildApproved = (): ApprovedSuggestion[] =>
    suggestions
      .filter(s => approvedSet.has(s.field_name))
      .map(s => ({
        field_name: s.field_name,
        suggested_rule: editedRules[s.field_name] ?? s.suggested_rule,
      }));

  const handleApply = async (): Promise<void> => {
    const approved: ApprovedSuggestion[] = buildApproved();
    if (approved.length === 0) return;
    setApplying(true);
    try {
      await onApply(approved);
    } finally {
      setApplying(false);
    }
  };

  const approvedCount = suggestions.filter(s => approvedSet.has(s.field_name)).length;

  return (
    <Dialog open={open} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoFixHighIcon />
          <Typography sx={styles.title}>AI取得ルール改善提案</Typography>
        </Stack>
        <IconButton onClick={onClose} disabled={applying} sx={{ padding: 0 }}>
          <Close sx={{ height: '32px', width: '32px' }} />
        </IconButton>
      </Box>

      {loading ? (
        <Box sx={styles.noSuggestionsBox}>
          <Typography sx={regularMd}>AIが改善案を生成中です...</Typography>
        </Box>
      ) : suggestions.length === 0 ? (
        <Box sx={styles.noSuggestionsBox}>
          <Typography sx={regularMd}>改善案はありませんでした。</Typography>
        </Box>
      ) : (
        <>
          <Typography sx={styles.subtitle}>
            編集履歴をもとに、AIが取得ルールの改善案を生成しました。内容を確認・編集し、適用する項目を選択してください。
          </Typography>
          <Stack direction="row" spacing={1} sx={{ marginBottom: '16px' }}>
            <Button sx={styles.selectAllButton} onClick={handleSelectAll}>
              <Typography sx={{ ...boldMd, color: gray80 }}>すべて選択</Typography>
            </Button>
            <Button sx={styles.selectAllButton} onClick={handleDeselectAll}>
              <Typography sx={{ ...boldMd, color: gray80 }}>すべて解除</Typography>
            </Button>
          </Stack>
          <Stack spacing={2} sx={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto', pr: 0.5 }}>
            {suggestions.map(suggestion => {
              const isApproved = approvedSet.has(suggestion.field_name);
              return (
                <Box
                  key={suggestion.field_name}
                  sx={{
                    ...styles.suggestionCard,
                    borderColor: isApproved ? '#1976d2' : gray40,
                    backgroundColor: isApproved ? '#f0f7ff' : 'transparent',
                    cursor: 'pointer',
                  }}
                  onClick={(): void => toggleApproval(suggestion.field_name)}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ marginBottom: '12px' }}
                  >
                    <Typography sx={styles.fieldName}>{suggestion.field_name}</Typography>
                    <Chip
                      icon={<CheckCircleOutlineIcon />}
                      label={isApproved ? '適用する' : '適用しない'}
                      color={isApproved ? 'primary' : 'default'}
                      size="small"
                      onClick={(e): void => {
                        e.stopPropagation();
                        toggleApproval(suggestion.field_name);
                      }}
                    />
                  </Stack>
                  <Divider sx={{ marginBottom: '12px' }} />
                  <Stack spacing={1.5}>
                    <Box onClick={(e): void => e.stopPropagation()}>
                      <Typography
                        sx={{ ...styles.ruleLabel, color: '#1976d2', marginBottom: '6px' }}
                      >
                        改善後の取得ルール
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        variant="outlined"
                        value={editedRules[suggestion.field_name] ?? suggestion.suggested_rule}
                        onChange={(e): void =>
                          handleRuleChange(suggestion.field_name, e.target.value)
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            ...regularMd,
                            color: '#1976d2',
                            backgroundColor: 'white',
                            padding: '0 !important',
                            '& .MuiOutlinedInput-input': {
                              padding: '10px 12px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      />
                    </Box>
                    {suggestion.current_rule && (
                      <Box>
                        <Typography sx={{ ...styles.ruleLabel, fontSize: '11px' }}>
                          変更前の取得ルール
                        </Typography>
                        <Typography sx={{ ...regularSm, color: gray80 }}>
                          {suggestion.current_rule}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography sx={styles.ruleLabel}>改善理由</Typography>
                      <Typography sx={{ ...regularSm, color: gray80 }}>
                        {suggestion.reason}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
          <Box sx={styles.buttonWrapper}>
            <Button sx={styles.cancelButton} onClick={onClose} disabled={applying}>
              <Typography sx={{ ...boldMd, color: gray80 }}>キャンセル</Typography>
            </Button>
            {/* 子パターンとして保存（#2967）。
                この帳票だけの問題なら差分へ、全体の問題なら親へ、をここで選べるようにする */}
            {onSaveAsSubPattern && (
              <Button
                sx={styles.cancelButton}
                onClick={(): void => onSaveAsSubPattern(buildApproved())}
                disabled={applying || approvedCount === 0}
              >
                <Typography sx={{ ...boldMd, color: gray80 }}>子パターンとして保存</Typography>
              </Button>
            )}
            <Button
              sx={styles.applyButton}
              onClick={handleApply}
              disabled={applying || approvedCount === 0}
            >
              <Typography sx={{ ...boldMd, color: 'white' }}>
                {applying ? '適用中...' : `親パターンに${approvedCount}件を適用`}
              </Typography>
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default PatternRuleImprovementModal;
